/* eslint-disable @typescript-eslint/no-explicit-any */
import { type Condition, SortOrder } from '@repo/api-gw-sdk';
import { ConditionOperatorEnum } from '@repo/api-gw-sdk';
import { v4 as uuid } from 'uuid';

import type { DemoSettings } from '@/contexts/environment';
import { isValueCondition } from '@/types/advanceFilter';
import { decodePageIndex, encodePageToken, parseJson } from '@/utils/string';

export function buildTokenPaginatedResponse<T extends { id: string }>(
  searchParams: URLSearchParams,
  body: { sorts: { field: string; order: SortOrder }[]; filter: unknown },
  data: T[]
) {
  const { pageToken, pageSize: pageSizeStr } = Object.fromEntries(searchParams);

  if (pageToken === '') {
    throw new Error(
      'parameter "pageToken" in query has an error: empty value is not allowed'
    );
  }

  const result = data;
  const sortField = body?.sorts?.[0]?.field;
  const sortDir = body?.sorts?.[0]?.order;

  if (
    typeof sortField === 'string' &&
    (sortDir === SortOrder.Desc || sortDir === SortOrder.Asc)
  ) {
    result.sort((a: any, b: any) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      if (aValue > bValue) {
        return sortDir === SortOrder.Desc ? -1 : 1;
      }
      if (aValue < bValue) {
        return sortDir === SortOrder.Desc ? 1 : -1;
      }
      return 0;
    });
  }

  const pageIndex = decodePageIndex(pageToken);
  const pageSize = +pageSizeStr || 25;

  const startIndex = pageIndex * pageSize;
  const endIndex = (pageIndex + 1) * pageSize;
  const recordsCount = result.length;

  return {
    data: result.slice(startIndex, endIndex),
    totalCount: recordsCount,
    nextToken:
      endIndex < recordsCount ? encodePageToken(pageIndex + 1) : undefined,
  };
}

export function buildPaginatedResponse<T>(
  searchParams: URLSearchParams,
  data: T[]
) {
  const {
    sort,
    pageIndex: pageIndexStr,
    pageSize: pageSizeStr,
    filter,
  } = Object.fromEntries(searchParams);

  const result = filterResults(data, filter);
  const [sortField, sortDir] = (sort || '').split(':');

  if (typeof sortField === 'string' && typeof sortDir === 'string') {
    result.sort((a: any, b: any) => {
      const aValue = getProperty(a, sortField);
      const bValue = getProperty(b, sortField);
      if (aValue > bValue) {
        return sortDir === 'desc' ? -1 : 1;
      }
      if (aValue < bValue) {
        return sortDir === 'desc' ? 1 : -1;
      }
      return 0;
    });
  }

  const pageIndex = +pageIndexStr || 0;
  const pageSize = +pageSizeStr || 25;

  const startIndex = pageIndex * pageSize;
  const endIndex = (pageIndex + 1) * pageSize;
  const recordsCount = result.length;

  return {
    data: result.slice(startIndex, endIndex),
    pagination: {
      recordsCount,
    },
  };
}

const filterResults = <T>(data: T[], filter: string | undefined) => {
  const condition = parseJson<Condition>(filter);
  if (!condition) {
    return data;
  }

  const conditions = isValueCondition(condition)
    ? [condition]
    : condition.conditions || [];

  return data.filter((item) => {
    return conditions.every((condition) => {
      if (isValueCondition(condition)) {
        const { property, operator, value } = condition;
        const itemValue = getProperty(item as any, property || '');
        if (!itemValue) {
          return false;
        }

        switch (operator) {
          case ConditionOperatorEnum.Contains:
          case ConditionOperatorEnum.Equals:
            return itemValue.toLowerCase().includes(value?.[0]?.toLowerCase());
          case ConditionOperatorEnum.In:
            return Array.isArray(itemValue)
              ? value?.some((x) => itemValue.some((y) => y === x))
              : value?.includes(itemValue);
          case ConditionOperatorEnum.NotEquals:
            return !itemValue.toLowerCase().includes(value?.[0]?.toLowerCase());
          case ConditionOperatorEnum.NotIn:
            return Array.isArray(itemValue)
              ? !value?.some((x) => itemValue.some((y) => y === x))
              : !value?.includes(itemValue);
          case ConditionOperatorEnum.ContainsAnyOf:
            if ('includes' in itemValue) {
              return value?.some(
                (x) =>
                  itemValue.includes(x) ||
                  itemValue.map((y: any) => y.id).includes(x) ||
                  itemValue.map((y: any) => y.name).includes(x)
              );
            } else {
              return value?.some((x) => {
                const parts = x.split('=');
                return (
                  itemValue[parts[0]] !== undefined &&
                  itemValue[parts[0]] === (parts[1] || '')
                );
              });
            }
        }
      }
    });
  });
};

const getProperty = (item: any, property: string) => {
  const value = item[property];
  if (value) {
    return value;
  }

  const alternativeGetter = accessorMappingAlternatives[property];
  return alternativeGetter?.(item);
};

const accessorMappingAlternatives: Record<
  string,
  (item: any) => string | undefined
> = {
  environment: (item) => item.classifications?.environmentDetails?.environment,
  dataClasses: (item) => item.classifications?.dataClassesDetails?.dataClasses,
  apps: (item) => item.classifications?.appsDetails?.apps,
  sourceRegion: (item) => item.region || item.resourceDetails?.region,
  securityScanConclusion: (item) => item.securityScan?.conclusion,
  clusterName: (item) =>
    item.resourceProperties?.eksNamespaceProperties?.clusterName,
  sourceStorageSize: (item) => item.sourceStorage?.sizeBytes,
  eonSnapshotCount: (item) => item.snapshotStorage?.eonSnapshotCount,
  genericSnapshotsDistinctDateCount: (item) =>
    item.snapshotStorage?.nonEonSnapshotCount,
  resourceId: (item) =>
    item.providerResourceId || item.resourceDetails?.providerResourceId,
  backupStorageSize: (item) => item.snapshotStorage?.sizeBytes,
  dateDiscovered: (item) => item.discoveredTime?.getTime(),
  createdTime: (item) => item.createdTime?.getTime(),
  cloud: (item) => item.cloudProvider, // todo: remove this once server change the property name for accounts.
  vaultId: (item) => item.vault?.id,
  backupCloudProvider: (item) => item.vault?.vaultAttributes?.cloudProvider,
  backupRegion: (item) => item.vault?.region,
  accountId: (item) => item.resourceDetails?.providerAccountId,
  sourceCloudProvider: (item) => item.resourceDetails?.cloudProvider,
  initiatorId: (item) => item.jobExecutionDetails?.onDemandInitiator?.id,
  resourceType: (item) =>
    item.resourceType || item.resourceDetails?.resourceType,
  resourceName: (item) =>
    item.resourceName || item.resourceDetails?.resourceName,
  jobId: (item) => item.jobExecutionDetails?.jobId,
  status: (item) => item.jobExecutionDetails?.status,
};

export const baseMockDalFactory = <T>(
  items: T[],
  itemIndexLocator: (items: T[], item: T) => number,
  list: (items: T[], demoSettings: DemoSettings) => T[]
) => ({
  list: (demoSettings: DemoSettings) => list(items, demoSettings),
  create: (entity: T) => {
    const newEntity = {
      ...entity,
      id: uuid(),
    };
    items.push(newEntity);
    return newEntity;
  },
  update: (entity: T) => {
    const index = itemIndexLocator(items, entity);

    if (index >= 0) {
      items[index] = {
        ...items[index],
        ...entity,
      };

      return items[index];
    }
  },
  delete: (entity: T) => {
    const index = itemIndexLocator(items, entity);
    if (index >= 0) {
      items.splice(index, 1);
    }
  },
});

export const mockDalFactory = <T extends { id: string }>(
  items: T[],
  list: (items: T[], demoSettings: DemoSettings) => T[]
) => {
  const base = baseMockDalFactory(
    items,
    (items, item) => items.findIndex((x) => x.id === item.id),
    list
  );

  return {
    ...base,
    update: (id: string, body: T) => {
      const item = items.find((x) => x.id === id);
      if (item) {
        return base.update({ ...body, id });
      }
    },
    delete: (id: string) => {
      const item = items.find((x) => x.id === id);
      if (item) {
        base.delete(item);
      }
    },
  };
};

function dateDiff(first: Date, second: Date) {
  return Math.round(
    (second.getTime() - first.getTime()) / (1000 * 60 * 60 * 24)
  );
}

export function convertDate(pivot: Date, date: Date) {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  const newDate = new Date(
    yesterday.getFullYear(),
    yesterday.getMonth(),
    yesterday.getDate(),
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
    date.getMilliseconds()
  );

  const daysToAdd = dateDiff(date, pivot);
  newDate.setDate(newDate.getDate() - daysToAdd);

  return newDate;
}
